import type { GenerateImageTaskData, SegmentTaskData, TaskQueue } from '@/typings/web-socket'
import { useImageStore } from '@/stores/image'
import { useBus } from '@/compositions/global'
import { BaInpaintTaskStatus } from '@/typings/ai'
import type { InpaintImageInfo } from '@/typings/image'
import metrics from '@/utils/metrics'
import { useI18n } from 'vue-i18n'
import { I18nKeys } from './i18n'

export enum ThemeKeys {
  /**
   * 浅色主题
   */
  LIGHT = 'light',
  /**
   * 深色主题
   */
  DARK = 'dark'
}

export enum EmitKeys {
  /**
   * 生成图片完成
   */
  GENERATED = 'generated',
  /**
   * 反选选区
   */
  INVERSE = 'inverse',
  /**
   * 图片语义分割
   */
  SEGMENT = 'segment',
  /**
   * 更新图片语义分割任务状态
   */
  UPDATE_SEGMENT_STATUS = 'update_segment_status',
  /**
   * 高清处理状态更新
   */
  UPDATE_HD_STATUS = 'update_hd_status',
  /**
   * 线稿处理状态更新
   */
  UPDATE_LINE_DRAFT_STATUS = 'update_line_draft_status',
  /**
   * 选区模式变化
   */
  TUNE_TYPE_CHANGE = 'tune_type_change',
  /**
   * 画笔大小变化
   */
  BRUSH_SIZE_CHANGE = 'brush_size_change',
  /**
   * 清空中间图片区域选区数据
   */
  CLEAR_CONTENT_DATA = 'clear_content_data',
  /**
   * 清空手动选区数据
   */
  CLEAR_MANUAL_DATA = 'clear_manual_data',
  /**
   * 撤回手动选区操作
   */
  UNDO_MANUAL_DATA = 'undo_manual_data',
  /**
   * 重做手动昂选区操作
   */
  REDO_MANUAL_DATA = 'redo_manual_data',
  /**
   * 抠图
   */
  UPDATE_MATTE_STATUS = 'update_matte_status',
  /**
   * 文生图
   */
  UPDATE_TEXT2_IMAGE_STATUS = 'update_text_2_image_status',
  /**
   * 图案处理
   */
  UPDATE_PATTERN_STATUS = 'update_pattern_status',
  /**
   * 去前景
   */
  UPDATE_OBJECT_REMOVAL_STATUS = 'update_object_removal_status',
  /**
   * 去背景
   */
  UPDATE_REMOVE_BG = 'update_remove_bg',
  /**
   * 触发开通会员页面
   */
  SHOW_SUBSCRIBE = 'show_subscribe',
  /**
   * 触发关于我们页面
   */
  SHOW_QRCODE = 'show_qrcode',
  /**
   * 展开指定教程
   */
  SHOW_GUIDE = 'show_guide',
  /**
   * 触发免费领取15天会员页面
   */
  SHOW_GIFT = 'show_gift',
  /**
   * 图片拖拽开始
   */
  IMAGE_DRAG_START = 'image_drag_start',
  /**
   * 图片拖拽结束
   */
  IMAGE_DRAG_END = 'image_drag_end',
  /**
   * 保存图片删除
   */
  SAVED_IMAGE_REMOVE = 'saved_image_remove',
  /**
   * 图片保存
   */
  SAVE_IMAGE = 'save_image',
  /**
   * 图片保存成功
   */
  SAVE_IMAGE_SUCCESS = 'save_image_success',
  /**
   * 图片保存之后
   */
  AFTER_IMAGE_SAVED = 'after_image_saved',
  /**
   * 退出登录
   */
  LOGOUT = 'logout',
  /**
   * 到达生图频率限制
   */
  LIMIT = 'limit',
  /**
   * 检查更新
   */
  SHOW_ABOUT = 'show_about',
  /**
   * 显示APP下载引导
   */
  SHOW_APP_DOWNLOAD_GUIDE = 'show_app_download_guide',
  /**
   * 显示绑定手机对话框
   */
  SHOW_BIND_PHONE = 'show_bind_phone',
  /**
   * 显示创建项目对话框
   */
  SHOW_CREATE_PROJECT = 'show_create_project',
  /**
   * 进入第一个项目，存在的话
   */
  ENTER_FIRST_PROJECT = 'enter_first_project',
  /**
   * 切换tab
   */
  CHANGE_TAB = 'change_tab',
  /**
   * 验证引导任务
   */
  VALIDATE_CAMPAIGN = 'validate_campaign',
  /**
   * 闪一下任务引导
   */
  FLASH_TASK_GUIDE = 'flash_task_guide',
  /**
   * 撒花
   */
  SHOW_CONFETTI = 'show_confetti',
  /**
   * 初始化线稿生态图引导数据
   */
  INIT_GUIDE_DATA = 'init_guide_data',
  /**
   * 初始化款式延伸引导数据
   */
  INIT_STYLE_EXTENSION_DATA = 'init_style_extension_data',
  /**
   * 初始化风格融合引导数据
   */
  INIT_STYLE_FUSION_DATA = 'init_style_fusion_data',
  /**
   * 加载免费使用次数
   */
  LOAD_FREE_USAGE = 'load_free_usage',
  /**
   * 显示免费使用次数限制对话框
   */
  SHOW_FREE_COUNT_LIMIT_DIALOG = 'show_free_count_limit_dialog',
  /**
   * 读取优惠券
   */
  LOAD_COUPON = 'load_coupon',
  /**
   * 显示花型贴图
   */
  SHOW_COMPOSITOR_CANVAS = 'show_compositor_canvas',
  /**
   * 隐藏花型贴图
   */
  HIDE_COMPOSITOR_CANVAS = 'hide_compositor_canvas',
  /**
   * 添加背景图
   */
  COMPOSITOR_ADD_BG_IMAGE = 'compositor_add_bg_image',
  /**
   * 添加蒙版图
   */
  COMPOSITOR_ADD_MASK_IMAGE = 'compositor_add_mask_image',
  /**
   * 添加前景图
   */
  COMPOSITOR_ADD_FG_IMAGE = 'compositor_add_fg_image',
  /**
   * 添加深度图
   */
  COMPOSITOR_ADD_DEPTH_IMAGE = 'compositor_add_depth_image',
  /**
   * 移除蒙版图
   */
  COMPOSITOR_REMOVE_MASK_IMAGE = 'compositor_remove_mask_image',
  /**
   * 蒙版图移除
   */
  COMPOSITOR_MASK_REMOVED = 'compositor_mask_removed',
  /**
   * 移除前景图
   */
  COMPOSITOR_REMOVE_FG_IMAGE = 'compositor_remove_fg_image',
  /**
   * 移除前景图
   */
  COMPOSITOR_REMOVE_FG_IMAGE_BY_SELECTION = 'compositor_remove_fg_image_by_selection',
  /**
   * 重新排序前景图
   */
  COMPOSITOR_REORDER_FG_IMAGES = 'compositor_reorder_fg_images',
  /**
   * 切换前景图可见性
   */
  COMPOSITOR_TOGGLE_FG_IMAGE_VISIBILITY = 'compositor_toggle_fg_image_visibility',
  /**
   * 切换前景图锁定状态
   */
  COMPOSITOR_TOGGLE_FG_IMAGE_LOCK = 'compositor_toggle_fg_image_lock',
  /**
   * 选中前景图
   */
  COMPOSITOR_SELECT_FG_IMAGE = 'compositor_select_fg_image',
  /**
   * 选中前景图
   */
  COMPOSITOR_SELECT_FG_IMAGE_BY_CANVAS = 'compositor_select_fg_image_by_canvas',
  /**
   * 前景图添加
   */
  COMPOSITOR_FG_IMAGE_ADDED = 'compositor_fg_image_added',
  /**
   * 前景图锁定状态变化
   */
  COMPOSITOR_TOGGLE_FG_IMAGE_LOCK_CANVAS = 'compositor_toggle_fg_image_lock_canvas',
  /**
   * 前景图可见性变化
   */
  COMPOSITOR_TOGGLE_FG_IMAGE_VISIBILITY_CANVAS = 'compositor_toggle_fg_image_visibility_canvas',
  /**
   * 保存结果图
   */
  COMPOSITOR_SAVE_RESULT_IMAGE = 'compositor_save_result_image',
  /**
   * 切换前景显示图片
   */
  COMPOSITOR_TOGGLE_FG_IMAGE_SHOW_CUT_OUT_IMAGE = 'compositor_toggle_fg_image_show_cut_out_image',
  /**
   * 画布通知工具栏切换了前景显示的图片
   */
  COMPOSITOR_NOTIFY_FG_IMAGE_SHOW_CUT_OUT_IMAGE = 'compositor_notify_fg_image_show_cut_out_image',
  /**
   * 显示网络错误
   */
  SHOW_NETWORK_ERROR = 'show_network_error',
  /**
   * 生成按钮闪烁效果
   */
  FLASH_GENERATE_BUTTON = 'flash_generate_button',
  /**
   * 更新用户插件列表
   */
  UPDATE_ACCOUNT_PLUGINS = 'update_account_plugins',
  /**
   * 更新插件市场列表
   */
  UPDATE_PLUGIN_MARKET_LIST = 'update_plugin_market_list',
  /**
   * 更新点数数据
   */
  UPDATE_TOKEN_DETAIL = 'update_token_detail',
  /**
   * 更新点数流水
   */
  UPDATE_TOKEN_TRANSACTIONS = 'update_token_transactions',
  /**
   * 刷新用户信息
   */
  REFRESH_USER_INFO = 'refresh_user_info'
}

export enum Env {
  DEV = 'dev',
  PROD = 'prod'
}

/**
 * 临时图片最大数量
 */
export const MAX_TEMP_IMAGE_COUNT = 10

/**
 * 图片生成进度
 */
const timers: Record<string, number> = {}
export const createTimer = (id: string, item: InpaintImageInfo) => {
  timers[id] = setInterval(() => {
    if (item.remainTime && item.remainTime > 1) {
      item.remainTime--
    } else {
      clearInterval(timers[id])
    }
  }, 1000)
}

/**
 * WebSocket处理函数
 */
export const WebSocketHandler = (): {
  [key in WebSocketHandlerMap]: (data: SegmentTaskData | GenerateImageTaskData) => void
} => {
  const imageStore = useImageStore()
  const bus = useBus()
  return {
    /**
     * 图片语义分析
     * @param data
     */
    segmentHandler: (data: SegmentTaskData | GenerateImageTaskData) => {
      let q: TaskQueue
      if (
        !imageStore.taskQueue.some((queue) => {
          if (queue.queueId === data.taskId) {
            q = queue
            return true
          }
          return false
        })
      )
        return
      console.info('elementId', q!.resourceId)
      bus.emit(EmitKeys.UPDATE_SEGMENT_STATUS, { data, elementId: q!.resourceId })
    },
    /**
     * 抠图
     * @param data
     */
    matteHandler: (data: SegmentTaskData | GenerateImageTaskData) => {
      setTimeout(() => {
        // matte创建任务的时候没有imageId，监听taskId
        if (!imageStore.taskQueue.some((queue) => queue.queueId === data.taskId)) return

        bus.emit(EmitKeys.UPDATE_MATTE_STATUS, data)
      }, 500)
    },
    /**
     * 生成LOOK
     * @param data
     */
    generateImageHandler: (data: SegmentTaskData | GenerateImageTaskData) => {
      const d = data as GenerateImageTaskData
      if (
        !Array.isArray(d.images) ||
        d.images.length === 0 ||
        !imageStore.taskQueue.some((queue) => {
          // 生成LOOK任务taskId是一对多张图片的，所以用resourceId来作为任务标识
          return d.images.some((img) => queue.resourceId === img.resourceId)
        })
      )
        return

      for (const item of imageStore.footerImageInfoList) {
        const task = d.images[0]

        if (item.id === task.resourceId) {
          if (task.status === BaInpaintTaskStatus.completed) {
            clearInterval(timers[task.resourceId])
            imageStore.deleteTaskQueue(data.taskId, task.resourceId)
            const event = metrics.eventOfKey(`${data.taskId}-${task.resourceId}`)
            if (event) {
              event.finish({ success: true }).fire()
            }
            item.url = task.url
            item.projectImageId = task.id
          } else if (task.status === BaInpaintTaskStatus.failed) {
            clearInterval(timers[task.resourceId])
            imageStore.deleteTaskQueue(data.taskId, task.resourceId)
            const event = metrics.eventOfKey(`${data.taskId}-${task.resourceId}`)
            if (event) {
              event.finish({ success: false }).fire()
            }
            // 失败后会返还点数
            bus.emit(EmitKeys.UPDATE_TOKEN_DETAIL)
          } else if (task.status === BaInpaintTaskStatus.inProgress) {
            item.estimateTime = task.estimateTime
            item.remainTime = task.estimateTime
            createTimer(task.resourceId, item)
          }
          item.status = task.status
          console.info('Update generated image status', item.status, item.id)
          break // 找到匹配的元素后中断循环
        }
      }
    },
    /**
     * 高清放大
     * @param data
     */
    upscaleHandler: (data: SegmentTaskData | GenerateImageTaskData) => {
      const d = data as GenerateImageTaskData
      // 延迟一秒，防止图片未插入列表就收到推送信息
      setTimeout(() => {
        if (
          !Array.isArray(d.images) ||
          d.images.length === 0 ||
          !imageStore.taskQueue.some((queue) => {
            // 生成高清任务taskId是一对多张图片的，所以用resourceId来作为任务标识
            return d.images.some((img) => queue.resourceId === img.resourceId)
          })
        )
          return

        bus.emit(EmitKeys.UPDATE_HD_STATUS, data)
      }, 1000)
    },
    /**
     * 线稿渲染
     * @param data
     */
    detectHandler: (data: SegmentTaskData | GenerateImageTaskData) => {
      debugger
      setTimeout(async () => {
        // 线稿处理创建任务时没有imageId，监听taskId
        if (!imageStore.taskQueue.some((queue) => queue.queueId === data.taskId)) return

        bus.emit(EmitKeys.UPDATE_LINE_DRAFT_STATUS, data)
      }, 1000)
    },
    /**
     * 去背景
     * @param data
     */
    removeBgHandler: (data: SegmentTaskData | GenerateImageTaskData) => {
      setTimeout(async () => {
        // 线稿处理创建任务时没有imageId，监听taskId
        if (!imageStore.taskQueue.some((queue) => queue.queueId === data.taskId)) return

        bus.emit(EmitKeys.UPDATE_REMOVE_BG, data)
      }, 1000)
    },
    /**
     * 去前景
     * @param data
     */
    objectRemovalHandler: (data: SegmentTaskData | GenerateImageTaskData) => {
      setTimeout(() => {
        // objectRemoval创建任务的时候没有imageId，监听taskId
        if (!imageStore.taskQueue.some((queue) => queue.queueId === data.taskId)) return

        bus.emit(EmitKeys.UPDATE_OBJECT_REMOVAL_STATUS, data)
      }, 500)
    },
    /**
     * 生成背景
     * @param data
     */
    text2ImageHandler: (data: SegmentTaskData | GenerateImageTaskData) => {
      setTimeout(() => {
        if (!imageStore.taskQueue.some((queue) => queue.queueId === data.taskId)) return

        bus.emit(EmitKeys.UPDATE_TEXT2_IMAGE_STATUS, data)
      }, 500)
    },
    /**
     * 图案处理
     * @param data
     */
    patternHandler: (data: SegmentTaskData | GenerateImageTaskData) => {
      setTimeout(() => {
        // 图案处理创建任务时没有imageId，监听taskId
        if (!imageStore.taskQueue.some((queue) => queue.queueId === data.taskId)) return

        bus.emit(EmitKeys.UPDATE_PATTERN_STATUS, data)
      }, 500)
    }
  }
}

/**
 * WebSocket处理函数映射
 */
export enum WebSocketHandlerMap {
  segmentHandler = 'segmentHandler',
  matteHandler = 'matteHandler',
  generateImageHandler = 'generateImageHandler',
  upscaleHandler = 'upscaleHandler',
  detectHandler = 'detectHandler',
  removeBgHandler = 'removeBgHandler',
  objectRemovalHandler = 'objectRemovalHandler',
  text2ImageHandler = 'text2ImageHandler',
  patternHandler = 'patternHandler' 
}

export const DEFAULT_QRCODE = new URL('@/assets/images/default-qrcode.png', import.meta.url).href

export const CDN_URL = import.meta.env.VITE_CDN_URL

export const IS_INTL = import.meta.env.VITE_VERSION === 'intl'

export enum ErrorTaskType {
  NONE,
  IMAGE_GENERATE,
  UPSCALE,
  REALTIME_DESIGN
}

export const ErrorTaskTypeMapBuilder = () => {
  const { t } = useI18n()

  return {
    [ErrorTaskType.NONE]: '',
    [ErrorTaskType.IMAGE_GENERATE]: t(I18nKeys.taskType.generate),
    [ErrorTaskType.UPSCALE]: t(I18nKeys.taskType.upscale),
    [ErrorTaskType.REALTIME_DESIGN]: t(I18nKeys.taskType.realtimeDesign)
  }
}

export const QRCODE_PLACEHOLDER = new URL('@/assets/images/qrcode-placeholder.png', import.meta.url)
  .href

export const VIDEO_CDN_URL = `${import.meta.env.VITE_CDN_URL}/resource/app/video`